import * as React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { CircularProgress, Typography } from "@mui/material";
import { styled, lighten, darken } from "@mui/system";
import { useState, useEffect, useRef } from "react";

const GroupHeader = styled("div")(({ theme }) => ({
  position: "sticky",
  top: "-8px",
  padding: "4px 10px",
  color: theme.palette.primary.main,
  backgroundColor:
    theme.palette.mode === "light"
      ? lighten(theme.palette.primary.light, 0.85)
      : darken(theme.palette.primary.main, 0.8),
}));

const GroupItems = styled("ul")({
  padding: 0,
});

export default function CustomAutocomplete({
  fetchOptions,
  options,
  groupByKey,
  label,
  labelKey,
  onChange,
  fullWidth = true,
  multiple,
  isOptionEqualToValue,
  placeholder,
  error,
  validate,
  helpertext,
  value,
  minSearchLength = 3,
  debounceTime = 500, // delay (ms) sebelum trigger API
  ...rest
}) {
  const [asyncOptions, setAsyncOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const debounceRef = useRef(null);

  useEffect(() => {
    if (fetchOptions) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      if (inputValue.length < minSearchLength) {
        setAsyncOptions([]);
        return;
      }

      debounceRef.current = setTimeout(() => {
        let active = true;
        setLoading(true);

        fetchOptions(inputValue).then((results) => {
          if (active) {
            setAsyncOptions(results || []);
            setLoading(false);
          }
        });

        return () => {
          active = false;
        };
      }, debounceTime);

      return () => clearTimeout(debounceRef.current);
    }
  }, [inputValue, fetchOptions, minSearchLength, debounceTime]);

  return (
    <>
      <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
        {label}
      </Typography>
      <Autocomplete
        id="autocomplete"
        options={fetchOptions ? asyncOptions : options}
        groupBy={(option) => option[groupByKey]}
        getOptionLabel={(option) => option[labelKey]}
        sx={{ mb: "1rem" }}
        size="small"
        fullWidth={fullWidth}
        onChange={onChange}
        onInputChange={(e, newInput) => setInputValue(newInput)}
        isOptionEqualToValue={isOptionEqualToValue}
        disableCloseOnSelect={multiple ? true : false}
        multiple={multiple}
        renderInput={(params) => (
          <TextField
            {...params}
            label={""}
            placeholder={placeholder}
            size="small"
            error={error ? error : validate && !value}
            helperText={
              helpertext ? helpertext : validate && !value && `* harap diisi`
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderGroup={(params) => (
          <li key={params.key}>
            <GroupHeader>{params.group}</GroupHeader>
            <GroupItems>{params.children}</GroupItems>
          </li>
        )}
        value={value}
        {...rest}
      />
    </>
  );
}
